import { inject } from '@angular/core';
import { CanActivateChildFn, RedirectFunction, Router } from '@angular/router';
import {
  AuthSessionService,
  isSuperAdministrator,
} from '../../../core/auth/auth-session.service';
import {
  firstPermittedChildRoute,
  firstPermittedRoute,
  isRoutePermitted,
} from '../../../core/layout/sidebar/nav-items';

const NO_ACCESS_PATH = '/no-access';

function pathOf(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}

// Guard de UX, no de seguridad: evita que un admin logueado navegue (via URL)
// a una seccion que el sidebar no le muestra. La autorizacion real vive en el
// backend (gateway + identity-ms), este guard no la reemplaza.
export const routePermissionGuard: CanActivateChildFn = (_childRoute, state) => {
  const session = inject(AuthSessionService);
  const menu = session.menu();
  const path = pathOf(state.url);

  // Esta herramienta no depende del menú configurable: solo existe para el
  // rol administrador principal y el backend vuelve a validar el token.
  if (path === '/supervision') {
    return isSuperAdministrator(session.user())
      ? true
      : inject(Router).createUrlTree([NO_ACCESS_PATH]);
  }

  // Siempre accesible: es el destino de un rol sin permisos (menu vacio) y de
  // una seccion agrupadora sin vistas hijas; bloquearla generaria un ciclo
  // con firstPermittedRoute.
  if (path === NO_ACCESS_PATH) return true;
  if (!menu.length) return inject(Router).createUrlTree([NO_ACCESS_PATH]);

  if (isRoutePermitted(menu, path)) return true;

  // '/' es el destino inicial del login. Si el rol no tiene Dashboard, lo
  // enviamos a la primera vista de su menú. La misma salida evita ciclos al
  // intentar abrir manualmente una sección no autorizada.
  const fallbackRoute = firstPermittedRoute(menu);
  return fallbackRoute
    ? inject(Router).createUrlTree([fallbackRoute])
    : inject(Router).createUrlTree([NO_ACCESS_PATH]);
};

// El destino de una seccion agrupadora depende de las vistas hijas que tenga
// el rol logueado, no de una sub-ruta fija.
export function redirectToFirstPermittedChild(parentRoute: string): RedirectFunction {
  return () =>
    firstPermittedChildRoute(inject(AuthSessionService).menu(), parentRoute) ?? NO_ACCESS_PATH;
}
